const pad = (value) => String(value).padStart(2, '0');

export const localDateKey = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const todayInputValue = () => localDateKey(new Date());

export const analyticsDateRange = (fromDate, toDate) => {
  const start = new Date(`${fromDate}T00:00:00`);
  const endExclusive = new Date(`${toDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(endExclusive.getTime()) || start > endExclusive) {
    throw new Error('Choose a valid date range.');
  }
  endExclusive.setDate(endExclusive.getDate() + 1);
  return {
    startIso: start.toISOString(),
    endExclusiveIso: endExclusive.toISOString(),
  };
};

// Meta mirrors are separate records for the same action. Keep participation
// totals tied to first-party events so a mirrored join is not counted twice.
export const isJoinOrEntryEvent = (eventName) =>
  !String(eventName || '').startsWith('meta_') && /joined|entry_submitted|entry_claimed/.test(eventName || '');

export const summarizeAnalytics = (events, signupDays, payments) => {
  const signups = signupDays.reduce((sum, row) => sum + Number(row.signups || 0), 0);
  const paymentTotals = new Map();
  payments.filter((payment) => payment.status === 'succeeded').forEach((payment) => {
    const currency = String(payment.currency || 'eur').toLowerCase();
    if (!/^[a-z]{3}$/.test(currency) || payment.amount_cents == null) return;
    paymentTotals.set(currency, (paymentTotals.get(currency) || 0) + Number(payment.amount_cents || 0));
  });

  return {
    signups,
    activeUsers: new Set(events.map((event) => event.profile_id).filter(Boolean)).size,
    actions: events.length,
    otherConversions: events.filter((event) => isJoinOrEntryEvent(event.event_name)).length,
    paymentTotals,
  };
};

export const buildDailyTrend = (events, signupDays, fromDate, toDate) => {
  const days = new Map();
  const usersByDay = new Map();
  const current = new Date(`${fromDate}T00:00:00`);
  const last = new Date(`${toDate}T00:00:00`);
  while (current <= last) {
    const key = localDateKey(current);
    days.set(key, { date: key.slice(5), fullDate: key, actions: 0, signups: 0, joins: 0, users: 0 });
    current.setDate(current.getDate() + 1);
  }

  events.forEach((event) => {
    const key = localDateKey(event.occurred_at);
    const day = days.get(key);
    if (!day) return;
    day.actions += 1;
    if (isJoinOrEntryEvent(event.event_name)) day.joins += 1;
    if (event.profile_id) {
      const users = usersByDay.get(key) || new Set();
      users.add(event.profile_id);
      usersByDay.set(key, users);
    }
  });

  signupDays.forEach((row) => {
    const day = days.get(row.signup_day);
    if (day) day.signups += Number(row.signups || 0);
  });

  return Array.from(days.values()).map((day) => ({
    ...day,
    users: usersByDay.get(day.fullDate)?.size || 0,
  }));
};
